import DateUtil from "./DateUtil";
import FieldUtil from "./FieldUtil";

export default class TableUtil {
    createRecord(table) {
        let document = {}
        let fields = table.fields

        // NOTE: the field "index" is actually its ID
        for (let i = 0; i < fields.length; i++) {
            let field = fields[i]

            if (new FieldUtil(field).isFixedCollaborator()) {
                document[field.index] = webase.managers.app.currentCollaborator()
            } else {
                document[field.index] = this.initField(field)
            }
        }

        return document
    }

    initField(field) {
        if (field.field_type === 'Date') {
            return new Date()
        } else if (field.field_type === 'Boolean') {
            return false
        } else if (field.field_type === 'Link to Object') {
            return {}  // this is stored as an internal Hash
        } else if (field.field_type === 'Attachment') {
            return []
        } else {
            return ''
        }
    }


    supportsPdf(table) {
        let textField = this.getTextField(table)

        if(textField && textField.metadata && textField.metadata.pdfExport) {
            return true
        }

        let attachmentField = this.getAttachmentField(table)

        if(attachmentField) {
            return true
        }

        return false
    }

    pdfLink(table, document) {
        let linkedObjectField = webase.utils.table.getLinkedObjectField(table)
        let relatedTable = webase.managers.app.getTable(linkedObjectField.related_model_id)

        let relatedObject = document[linkedObjectField.index]

        if (linkedObjectField.field_type === 'Collaborator') {
            relatedObject = document[linkedObjectField.index].name
        }

        let documentIds = Object.keys(relatedObject)
        let relatedDocumentId = documentIds.length > 0 ? documentIds[0] : null

        if (linkedObjectField && relatedTable&& relatedObject && relatedDocumentId) {
            return `/pdf/${relatedTable.id}/${relatedDocumentId}/${table.id}/${document['id']}`
        }

        return '#'
    }

    hasFixedCollaborator(table) {
        return this.getFixedCollaborator(table) !== null
    }

    getFixedCollaborator(table) {
        let collaboratorField = this.getCollaboratorField(table)

        if (collaboratorField) {
            let fieldUtil = new FieldUtil(collaboratorField)

            if (fieldUtil.isUserFixed()) {
                return collaboratorField
            }
        }

        return null
    }

    getCollaboratorField(table) {
        return this.getFieldByType(table, 'Collaborator')
    }

    getLinkedObjectField(table) {
        return this.getFieldByType(table, 'Link to Object')
    }

    hasSelectField(table) {
        return this.getSelectField(table) !== null
    }

    getSelectField(table) {
        return this.getFieldByType(table, 'Single Select')
    }

    hasImageField(table) {
        return this.getImageField(table) !== null
    }

    getImageField(table) {
        return this.getFieldByType(table, 'Image')
    }

    getDateField(table) {
        return this.getFieldByType(table, 'Date')
    }

    hasTextField(table) {
        return this.getTextField(table) !== null
    }

    getTextField(table) {
        return this.getFieldByType(table, 'Text')
    }

    getAttachmentField(table) {
        return this.getFieldByType(table, 'Attachment')
    }

    getChildTable(table) {
        let field = this.getLinkedObjectField(table)

        if (field['child_model_id']) {
            return webase.utils.array.get(webase.model.app.tables, field['child_model_id'])
        }

        return null
    }

    getGroupField(view, table) {
        let groupFields = table.fields.filter(field => (field.index === view.group_field_id))

        if (groupFields) {
            return groupFields[0]
        }

        return this.getSelectField(table)
    }

    getAvailableOperands(table) {
        let numericFields = this.getNumbericFields(table)

        if (table.parent) {
            let parentTable = webase.utils.array.get(webase.model.app.tables, table.parent)
            let parentNumericFields = this.getNumbericFields(parentTable)
            return numericFields.concat(parentNumericFields)
        }

        let childTable = this.getChildTable(table)

        if (childTable) {
            let childNumericFields = this.getNumbericFields(childTable)
            return numericFields.concat(childNumericFields)
        }

        return numericFields
    }

    getNumbericFields(table) {
        return table.fields.filter(field => (field.field_type === 'Number' || field.field_type === 'Dynamic'))
    }

    getListFields(table) {
        return table.fields.filter(field => (field.field_type !== 'Link to Object' && field.field_type !== 'Text'))
    }

    getFieldByType(table, fieldType) {
        if (table && table.fields.length > 0) {
            let fields = table.fields

            for (let i = 0; i < fields.length; i++) {
                let field = fields[i]

                if (field.field_type === fieldType) {
                    return field
                }
            }
        }

        return null
    }

    getFieldByName(table, fieldName) {
        return table.fields.filter(field => (field.name === fieldName))[0]
    }

    getPrimaryField(table) {
        if (table === null || table.fields.length === 0) {
            return {id: '', name: 'Name'}
        }

        return table.fields[0]
    }

    getRelatedField(table, relatedModelId) {
        return table.fields.filter(field => (field.related_model_id === relatedModelId))[0]
    }

    isLinkedObject(field) {
        return field.field_type === 'Link to Object'
    }

    isCollaborator(field) {
        return field.field_type === 'Collaborator'
    }

    isVisibleOnList(field) {
        return field.field_type !== 'Link to Object' && field.field_type !== 'Text'
    }

    getFieldColor(field, value) {
        let fieldOptions = field.metadata

        if (fieldOptions) {
            for (let i = 0; i < fieldOptions.length; i++) {

                let fieldOption = fieldOptions[i]

                if (fieldOption.value === value) {
                    return fieldOption.color
                }
            }
        }

        return '#6c757d'
    }

    hasFormView(table) {
        return this.hasView(table, 'form')
    }

    getFormView(table) {
        let formViews = table.views.filter(view => (view.view_type === 'form'))
        return formViews[0]
    }

    hasDetailsView(table) {
        return this.hasView(table, 'details')
    }

    getDetailsView(table) {
        let formViews = table.views.filter(view => (view.view_type === 'details'))
        return formViews[0]
    }

    hasView(table, viewType) {
        return table.views.filter(view => (view.view_type === viewType)).length > 0
    }

    isImage(attachment) {
        return attachment.file_type === 'image/jpeg' || attachment.file_type === 'image/jpg' ||
            attachment.file_type === 'image/png'
    }
}